import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import AccountAPI from '@/modules/account/constants/account.api.uri.const.ts'
import { formatTableDataAccount, formatTableDataKyc } from '../helper/table'
import { buildFilterObject } from '@/utils/filter'
import { markRaw } from 'vue'

export interface tabItem {
  id: string,
  label: string,
  active: boolean,
}

export enum AccountKYCActionTypes {
  FILTERING = 'FILTERING',
  PENDING= "PENDING",
  FETCHING= "FETCHING",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_FAILURE = "FETCH_FAILURE",
}

interface AccountKycListStore {
  action_type: AccountKYCActionTypes,
  action_mess: string,
  uuid: string,
  data: Object,
  filter: Object,
  pageSize: number,
  page: number,
  tabs: Array<any>,
  tableConfig: any,
  selectedIds: Array<string|number>,
}

export const useAccountKycListStore = defineStore('account.kyc', {
  state: (): AccountKycListStore => ({
    action_type: AccountKYCActionTypes.PENDING,
    action_mess: "",
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'code',
        type: 'text',
        label: 'Mã tài khoản',
        placeholder: 'Nhập mã tài khoản',
        value: '',
      },
      {
        key: 'keyword',
        type: 'taginput',
        label: 'Lọc theo từ khoá',
        placeholder: 'Nhập từ khoá',
        value: '',
      },

      {
        key: 'requestDate',
        type: 'date-range',
        label: 'Lọc theo ngày yêu cầu',
        placeholder: 'Chọn ngày yêu cầu',
        value: '',
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách yêu cầu',
        active: true,
      }
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        code: { label: 'CODE', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        phone: { label: 'SDT', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        email: { label: 'Email', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        full_name: { label: 'Tên', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        address: { label: 'Khu vực', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        kyc_request_date: { label: 'Ngày yêu cầu', component: markRaw(DateTimeCell), visible: true, sort: 'desc', config: {} },
      }
    },
    selectedIds: []
  }),
  getters: {
    sortFilter: state => {
      const sort: any = []
      if(Object.keys(state.tableConfig?.columns).length>0){
        Object.keys(state.tableConfig?.columns).forEach(col => {
          if(state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    }
  },
  actions: {
    //TAB ACTION
    changeTab (targetId: string) {

      for (const tab of this.tabs) {
        tab.active = (tab.id === targetId)
      }
    },
    closeTab (targetId: string) {
      const index = this.tabs.findIndex(tab => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab () {
      this.tabs = [{
        id: 'list',
        label: 'Danh sách tài khoản',
        active: true,
      }]
    },
    newTab (item: tabItem) {
      const exists = this.tabs.some(tab => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    //FILTER ACTION
    setFilter (filter: any) {
      this.filter = filter
      this.page = 1
      this.action_type = AccountKYCActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    resetFilter () {
      this.filter = this.filter.map(item => ({...item, value: ''}))
      this.page = 1
      this.action_type = AccountKYCActionTypes.FILTERING
      this.uuid = uuid.v4()
    },


    //PAGE ACTION
    changePage (page: number) {
      this.page = page
      this.action_type = AccountKYCActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize (size: number) {
      this.pageSize = size
      this.action_type = AccountKYCActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
        this.action_type = AccountKYCActionTypes.FILTERING
        this.uuid = uuid.v4()
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },

    //DATA ACTION
    async fetchList(){
      return new Promise(async (resolve, reject) => {
        try{
          this.action_type = AccountKYCActionTypes.FETCHING
          const sort = this.sortFilter
          const pagination = {
            page: this.page,
            page_size: this.pageSize,
            sort
          }
          const filter = buildFilterObject(this.filter, pagination);
          const res: any = await APIClient.get(AccountAPI.kyc.list, {
            params: {...filter, processing_status: 'pending'}
          })
          const { data } = res.data
          this.data = {
            list: formatTableDataKyc(data.data) || [],
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0
          }
          this.action_type = AccountKYCActionTypes.FETCH_SUCCESS
          resolve(res)
        } catch (e) {
          this.action_type = AccountKYCActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    }
  }
})
